import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '../api';
import DraggableModal from '../components/DraggableModal';
import StatusBadge from '../components/StatusBadge';
import ActionButton from '../components/ActionButton';
import ErrorAlert from '../components/ErrorAlert';
import {
  AttributeProposalItem,
  initialAttributeDraft,
  parseAttributeDraftValue,
} from '../components/AttributeProposalItem';
import {
  FieldProposalItem,
  SignalProposalItem,
  editableProposalValue,
  parseEditedProposalValue,
} from '../components/ProposalItems';
import {
  Badge,
  FilterChip,
  PROPOSAL_TIER_SECTIONS,
  ProposalLabelPill,
  Row,
  SOURCE_FILTERS,
  STATUSES,
  TIER_FILTERS,
  TYPE_FILTERS,
  facetChipText,
  facetCount,
  groupAttentionText,
  groupSourceText,
  groupTierLabel,
  isEditableShortcutTarget,
  labelize,
  optionRowsWithFacetValues,
  pillClass,
  proposalFieldId,
  proposalFieldRequestId,
  proposalHelpText,
  proposalKind,
  proposalKindLabel,
  proposalLabelMetaFromItems,
  proposalPayload,
  proposalSourceType,
  proposalStatus,
  proposalTier,
  proposalTierLabel,
  proposalTitle,
  sourceTone,
  typeTone,
  valueText,
} from './changeRequests/ChangeRequestParts';

function ProposalLensItem({ item, onSelectItem, onOpenPlace }) {
  const payload = proposalPayload(item);
  const kind = proposalKind(item);
  const cardAttributePayload = kind === 'card_attribute' ? { ...item, ...payload } : payload;
  const tier = proposalTier(item);
  const cardKey = item.card_key || payload.card_key;
  const placeMasterId = item.place_master_id || payload.place_master_id;
  const sourceUrl = payload.source_url || item.source?.url || payload.evidence?.source_url || item.evidence?.source_url;
  const isComputedWarning = kind === 'warning' && payload.clearing === 'computed';

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge kind={tier}>{proposalTierLabel(item)}</Badge>
        <span className={pillClass(typeTone(kind))}>
          {proposalKindLabel(kind)}
        </span>
        {cardKey && <span className={pillClass('blue')}>Card: {cardKey}</span>}
        <span className={pillClass(sourceTone(proposalSourceType(item)))}>
          {labelize(proposalSourceType(item))}
        </span>
      </div>
      <div className="mt-2">
        <p className="text-sm font-semibold text-stone-950">{proposalTitle(item)}</p>
        <p className="mt-1 text-xs leading-5 text-stone-500">{proposalHelpText(kind, tier)}</p>
      </div>

      {kind === 'field_update' && (
        <div className="mt-3 grid gap-3 rounded-lg border border-stone-100 bg-stone-50/70 p-3 text-xs md:grid-cols-2">
          <div>
            <p className="font-medium uppercase tracking-wide text-stone-400">Current</p>
            <p className="mt-1 break-words text-stone-500 line-through">{valueText(payload.old_value)}</p>
          </div>
          <div>
            <p className="font-medium uppercase tracking-wide text-stone-400">Proposed</p>
            <p className="mt-1 break-words text-stone-900">{valueText(payload.proposed_value)}</p>
          </div>
        </div>
      )}

      {kind === 'card_attribute' && (
        <div className="mt-3 rounded-lg border border-teal-100 bg-teal-50/50 p-3 text-xs">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <p className="font-medium uppercase tracking-wide text-teal-700">Current</p>
              <p className="mt-1 break-words text-stone-500">{valueText(cardAttributePayload.current_value)}</p>
            </div>
            <div>
              <p className="font-medium uppercase tracking-wide text-teal-700">Proposed</p>
              <p className="mt-1 break-words text-stone-900">{valueText(cardAttributePayload.proposed_value)}</p>
            </div>
          </div>
          {cardAttributePayload.overrides_inherited && (
            <p className="mt-2 break-words text-amber-800">
              Inherited place value: {valueText(cardAttributePayload.inherited_value)}
            </p>
          )}
        </div>
      )}

      {kind === 'signal' && (payload.value || payload.evidence_snippet) && (
        <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50/50 p-3 text-xs text-stone-700">
          {payload.value && <p><span className="text-stone-400">Value:</span> {labelize(payload.value)}</p>}
          {payload.evidence_snippet && <p className="mt-1">{payload.evidence_snippet}</p>}
        </div>
      )}

      {kind === 'warning' && (
        <div className={`mt-3 rounded-lg border p-3 text-xs ${isComputedWarning ? 'border-yellow-200 bg-yellow-50 text-yellow-800' : 'border-red-100 bg-red-50 text-red-800'}`}>
          {isComputedWarning
            ? 'Fix the underlying card field; this computed item clears automatically.'
            : (payload.blast_radius || payload.warning_code || 'Review this asserted warning.')}
        </div>
      )}

      {sourceUrl && (
        <a href={sourceUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex break-all text-xs font-medium text-blue-700 hover:underline">
          Evidence source
        </a>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {isComputedWarning ? (
          <ActionButton variant="primary" onClick={() => onOpenPlace?.(placeMasterId, cardKey || 'primary')} disabled={!placeMasterId}>
            Fix in Workbench
          </ActionButton>
        ) : (
          <ActionButton variant="primary" onClick={() => onSelectItem?.(item)}>
            Review proposal
          </ActionButton>
        )}
        {placeMasterId && !isComputedWarning && (
          <ActionButton variant="secondary" onClick={() => onOpenPlace?.(placeMasterId, cardKey || 'primary')}>
            Open card
          </ActionButton>
        )}
      </div>
    </div>
  );
}

function ProposalLensSections({ items, loading, emptyCopy, onSelectItem, onOpenPlace }) {
  if (loading) {
    return <div className="flex items-center justify-center py-16 text-sm text-stone-400">Loading proposals...</div>;
  }
  if (!items.length) {
    return <p className="rounded-xl border border-stone-200 bg-white p-6 text-sm text-stone-400">{emptyCopy || 'No proposals found.'}</p>;
  }
  return (
    <div className="space-y-5">
      {PROPOSAL_TIER_SECTIONS.map(([tier, title, helper]) => {
        const sectionItems = items.filter(item => proposalTier(item) === tier);
        if (sectionItems.length === 0) return null;
        return (
          <section key={tier} className="space-y-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge kind={tier}>{title}</Badge>
                <span className="text-xs font-medium text-stone-400">{sectionItems.length}</span>
              </div>
              <p className="mt-1 text-xs text-stone-500">{helper}</p>
            </div>
            <div className="grid gap-3">
              {sectionItems.map(item => (
                <ProposalLensItem key={item.id} item={item} onSelectItem={onSelectItem} onOpenPlace={onOpenPlace} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ProposalItemModal({
  item,
  onClose,
  onActionComplete,
  queueActive = false,
  queueProgress = '',
  onSkipQueueItem,
  onPreviousQueueItem,
  onOpenPlace,
}) {
  const payload = proposalPayload(item);
  const kind = proposalKind(item);
  const [mergeId, setMergeId] = useState('');
  const [note, setNote] = useState(payload.reviewer_note || payload.review_note || '');
  const [noteOpen, setNoteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState(null);
  const [editingField, setEditingField] = useState(false);
  const [fieldDraft, setFieldDraft] = useState('');
  const [editingAttribute, setEditingAttribute] = useState(false);
  const [attributeDraft, setAttributeDraft] = useState('');
  const [attributeRejectReason, setAttributeRejectReason] = useState('');
  const [cardDraft, setCardDraft] = useState({});
  const [cardEditOpen, setCardEditOpen] = useState(false);
  const [warningVerdict, setWarningVerdict] = useState(null);
  const [warningEvidence, setWarningEvidence] = useState('');
  const [warningDismissReason, setWarningDismissReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setNote(payload.reviewer_note || payload.review_note || '');
    setNoteOpen(false);
    setWarningVerdict(null);
    setWarningEvidence('');
    setWarningDismissReason('');
    setEditingAttribute(false);
    setAttributeDraft('');
    setAttributeRejectReason('');
    setError('');
  }, [item?.id, payload.reviewer_note, payload.review_note]);

  const reloadAndMaybeClose = async (close = false) => {
    await onActionComplete?.({ close });
  };

  const requestId = ['new_listing', 'card_create'].includes(kind) ? item.source_item_id : proposalFieldRequestId(item);
  const fieldId = proposalFieldId(item);
  const field = kind === 'field_update' ? { ...payload, id: fieldId } : null;
  const attributeSourceId = item.source_item_id
    || payload.source_item_id
    || (typeof payload.id === 'number' ? payload.id : null)
    || (String(item.id || '').match(/^card_attribute:(\d+)$/)?.[1] ?? null);
  const attribute = kind === 'card_attribute'
    ? { ...item, ...payload, id: attributeSourceId || payload.id || item.id }
    : null;
  const tier = proposalTier(item);
  const placeName = item.place_name || payload.place_name;
  const placeMasterId = item.place_master_id || payload.place_master_id;
  const cardKey = item.card_key || payload.card_key;
  const requiresResolveEvidence = Boolean(payload.writes_verification_provenance);
  const sourceType = proposalSourceType(item);
  const sourceUrl = payload.source_url || item.source?.url || payload.evidence?.source_url || item.evidence?.source_url;
  const blastRadius = payload.blast_radius || item.blast_radius;
  const scope = payload.scope || item.scope;
  const isComputedWarning = kind === 'warning' && payload.clearing === 'computed';
  const proposedCard = useMemo(
    () => payload.proposed_card || payload.raw_payload?.proposed_card || payload.raw_payload?.card || {},
    [payload],
  );
  const existingCards = payload.existing_cards || [];
  const activeCardDraft = cardEditOpen ? cardDraft : proposedCard;

  useEffect(() => {
    setCardDraft(proposedCard || {});
    setCardEditOpen(false);
  }, [item?.id, proposedCard]);

  const patchRequest = async (overallStatus) => {
    setSaving(true);
    setError('');
    try {
      await api.patchChangeRequest(requestId, { overall_status: overallStatus, reviewer_note: note });
      await reloadAndMaybeClose(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const createListing = async () => {
    setSaving(true);
    setError('');
    try {
      await api.createListingFromChangeRequest(requestId, { admin_notes: note });
      await reloadAndMaybeClose(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const mergeListing = async () => {
    setSaving(true);
    setError('');
    try {
      await api.mergeChangeRequest(requestId, { place_master_id: mergeId, reviewer_note: note });
      await reloadAndMaybeClose(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const createCardFromProposal = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.createCardFromChangeRequest(requestId, { ...activeCardDraft, reviewer_note: note, admin_notes: note });
      const createdCardKey = res?.created_card?.card_key || res?.created_card?.card?.card_key;
      await reloadAndMaybeClose(true);
      if (placeMasterId && createdCardKey && onOpenPlace) {
        onOpenPlace(placeMasterId, createdCardKey);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateCardDraft = (field, value) => {
    setCardDraft(current => ({ ...current, [field]: value }));
  };

  const mergeCardCreate = async () => {
    setSaving(true);
    setError('');
    try {
      await api.mergeCardCreateChangeRequest(requestId, { card_key: mergeId, reviewer_note: note });
      await reloadAndMaybeClose(true);
      if (placeMasterId && mergeId.trim() && onOpenPlace) {
        onOpenPlace(placeMasterId, mergeId.trim());
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const reviewField = async (fieldStatus, proposedValue) => {
    setSavingAction(fieldStatus);
    setError('');
    try {
      const body = { field_status: fieldStatus, reviewer_note: note, apply: fieldStatus === 'approved' };
      if (proposedValue !== undefined) body.proposed_value = proposedValue;
      await api.reviewChangeRequestField(requestId, fieldId, body);
      setEditingField(false);
      setFieldDraft('');
      await reloadAndMaybeClose(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingAction(null);
    }
  };

  const reviewAttribute = async (verdict) => {
    if (!attribute?.id) {
      setError('This attribute proposal is missing its source id.');
      return;
    }
    const body = { verdict, reviewer_note: note };
    if (verdict === 'edit_and_approve') {
      try {
        body.edited_value = parseAttributeDraftValue(attribute, attributeDraft);
      } catch (e) {
        setError(e.message);
        return;
      }
    }
    if (verdict === 'reject') {
      if (!attributeRejectReason) {
        setError('Choose a rejection reason.');
        return;
      }
      body.rejection_reason = attributeRejectReason;
    }
    setSavingAction(verdict);
    setError('');
    try {
      await api.reviewCardAttributeProposal(attribute.id, body);
      setEditingAttribute(false);
      setAttributeDraft('');
      setAttributeRejectReason('');
      await reloadAndMaybeClose(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingAction(null);
    }
  };

  const applyEditedField = async () => {
    let parsed;
    try {
      parsed = parseEditedProposalValue(fieldDraft, field.proposed_value);
    } catch (e) {
      setError(e.message);
      return;
    }
    await reviewField('approved', parsed);
  };

  const reviewSignal = async (verdict, options = {}) => {
    const defaultSignalIds = item.source_item_ids || payload.source_item_ids || [item.source_item_id || payload.id].filter(Boolean);
    const signalIds = options.signalIds || defaultSignalIds;
    const signalId = options.signalId || signalIds[0] || payload.id;
    const cardKey = item.card_key || payload.card_key;
    if (['add_to_agent_note', 'propose_tag', 'approve_match_signals'].includes(verdict) && !cardKey) {
      setError('Open the relevant card before applying a place-scoped signal.');
      return;
    }
    setSavingAction(options.savingKey || verdict);
    setError('');
    try {
      await api.reviewSignalProposal(signalId, { verdict, card_key: cardKey, signal_ids: signalIds, reviewer_note: note });
      await reloadAndMaybeClose(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingAction(null);
    }
  };

  const openPlace = () => {
    if (!placeMasterId || !onOpenPlace) return;
    onOpenPlace(placeMasterId, cardKey || 'primary');
  };

  const copyTechnicalId = () => {
    navigator.clipboard?.writeText?.(String(item.id || '')).catch(() => {});
  };

  const reviewWarning = async (verdict) => {
    let body = { verdict };
    if (verdict === 'resolve') {
      const evidence = warningEvidence.trim();
      if (requiresResolveEvidence && !evidence) {
        setError('Add evidence or a source before resolving this verification warning.');
        return;
      }
      if (evidence) body = { ...body, evidence_note: evidence };
    } else if (verdict === 'dismiss') {
      const reason = warningDismissReason.trim() || note.trim();
      if (!reason) {
        setError('Add a dismissal reason so this decision is auditable.');
        return;
      }
      body = { ...body, reason };
    }
    const warningPlaceMasterId = placeMasterId;
    const warningCardKey = cardKey || 'primary';
    const scope = payload.scope || item.scope || 'card';
    const warningCode = payload.warning_code;
    if (!warningPlaceMasterId || !warningCode) {
      setError('This warning is missing place or warning identity.');
      return;
    }
    setSavingAction(verdict);
    setError('');
    try {
      await api.reviewWarningProposal(warningPlaceMasterId, warningCardKey, scope, warningCode, body);
      await reloadAndMaybeClose(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingAction(null);
    }
  };

  return (
    <DraggableModal isOpen onClose={onClose} title={placeName || proposalKindLabel(kind)} initialWidth={900} initialHeight={720}>
      <div className="h-full overflow-y-auto p-5">
        <div className="space-y-5">
          <div className="space-y-3 border-b border-stone-200 pb-4">
            <div className="flex flex-wrap items-center gap-2">
              {placeMasterId ? (
                <button
                  type="button"
                  onClick={openPlace}
                  className="text-left text-lg font-semibold text-stone-950 underline-offset-2 hover:underline"
                >
                  {placeName || placeMasterId}
                </button>
              ) : (
                <h3 className="text-lg font-semibold text-stone-950">Unattached proposal</h3>
              )}
              {cardKey && <span className={pillClass('blue')}>Card: {cardKey}</span>}
              <Badge kind={tier}>{labelize(tier)}</Badge>
              <span className={pillClass(sourceTone(sourceType))}>{labelize(sourceType)}</span>
              <StatusBadge status={proposalStatus(item)} />
            </div>
            <div>
              <p className="text-base font-medium text-stone-900">{proposalTitle(item)}</p>
              <p className="mt-1 text-sm text-stone-500">{proposalKindLabel(kind)}</p>
              {blastRadius && <p className="mt-1 text-sm font-medium text-red-700">{blastRadius}</p>}
            </div>
          </div>

          {error && <ErrorAlert error={error} onClose={() => setError('')} />}

          {queueActive && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
              <span className="font-medium">Queue · {queueProgress}</span>
              <ActionButton variant="secondary" onClick={onPreviousQueueItem}>Previous</ActionButton>
              <ActionButton variant="secondary" onClick={onSkipQueueItem}>Skip</ActionButton>
            </div>
          )}

          {kind === 'new_listing' && (
            <>
              <div className="flex flex-wrap gap-2">
                <ActionButton variant="primary" onClick={createListing} loading={saving}>Create Listing</ActionButton>
                <ActionButton variant="secondary" onClick={() => patchRequest('needs_followup')} loading={saving}>Needs Follow-up</ActionButton>
                <ActionButton variant="danger" onClick={() => patchRequest('rejected')} loading={saving}>Reject</ActionButton>
                <ActionButton variant="secondary" onClick={onClose}>Close</ActionButton>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">Merge with existing place</label>
                <div className="mt-2 flex gap-2">
                  <input
                    value={mergeId}
                    onChange={event => setMergeId(event.target.value)}
                    placeholder="pocket-host-place-00000"
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  />
                  <ActionButton variant="secondary" onClick={mergeListing} disabled={!mergeId.trim()} loading={saving}>Merge</ActionButton>
                </div>
              </div>
            </>
          )}

          {kind === 'card_create' && (
            <>
              <div className="flex flex-wrap gap-2">
                <ActionButton variant="primary" onClick={createCardFromProposal} loading={saving}>Create Card</ActionButton>
                <ActionButton variant="secondary" onClick={() => setCardEditOpen(value => !value)} disabled={saving}>
                  {cardEditOpen ? 'Use original' : 'Edit and create'}
                </ActionButton>
                <ActionButton variant="secondary" onClick={() => patchRequest('needs_followup')} loading={saving}>Needs Follow-up</ActionButton>
                <ActionButton variant="danger" onClick={() => patchRequest('rejected')} loading={saving}>Reject</ActionButton>
                <ActionButton variant="secondary" onClick={onClose}>Close</ActionButton>
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className={pillClass('violet')}>New card</span>
                  <p className="text-sm font-medium text-stone-900">{valueText(activeCardDraft.card_title || activeCardDraft.card_key)}</p>
                </div>
                <div className="grid gap-1 md:grid-cols-2">
                  <Row label="Card key" value={activeCardDraft.card_key} />
                  <Row label="Card title" value={activeCardDraft.card_title} />
                  <Row label="Lane" value={activeCardDraft.retrieval_lane} />
                  <Row label="Category" value={activeCardDraft.category} />
                  <Row label="Tags" value={activeCardDraft.tags || activeCardDraft.guest_use_case_tags} />
                  <Row label="Utility kind" value={activeCardDraft.utility_kind} />
                </div>
                <Row label="Pitch" value={activeCardDraft.pitch} />
                <Row label="Knows" value={activeCardDraft.caveat} />
                <Row label="Agent note" value={activeCardDraft.card_note_for_agent} />
                {cardEditOpen && (
                  <div className="mt-4 grid gap-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      {[
                        ['card_key', 'Card key'],
                        ['card_title', 'Card title'],
                        ['retrieval_lane', 'Retrieval lane'],
                        ['category', 'Category'],
                        ['utility_kind', 'Utility kind'],
                        ['tags', 'Tags'],
                      ].map(([field, label]) => (
                        <label key={field} className="block">
                          <span className="text-xs font-medium text-stone-600">{label}</span>
                          <input
                            value={Array.isArray(cardDraft[field]) ? cardDraft[field].join(', ') : (cardDraft[field] || '')}
                            onChange={event => updateCardDraft(field, event.target.value)}
                            className="mt-1 w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                          />
                        </label>
                      ))}
                    </div>
                    {[
                      ['pitch', 'Pitch'],
                      ['caveat', 'Knows'],
                      ['card_note_for_agent', 'Agent note'],
                    ].map(([field, label]) => (
                      <label key={field} className="block">
                        <span className="text-xs font-medium text-stone-600">{label}</span>
                        <textarea
                          rows={2}
                          value={cardDraft[field] || ''}
                          onChange={event => updateCardDraft(field, event.target.value)}
                          className="mt-1 w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">Merge into existing card</label>
                <div className="mt-2 flex gap-2">
                  <select
                    value={mergeId}
                    onChange={event => setMergeId(event.target.value)}
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose card...</option>
                    {existingCards.map(card => (
                      <option key={card.card_key} value={card.card_key}>
                        {card.card_title || labelize(card.card_key)} · {card.card_key}
                      </option>
                    ))}
                  </select>
                  <ActionButton variant="secondary" onClick={mergeCardCreate} disabled={!mergeId.trim()} loading={saving}>Merge</ActionButton>
                </div>
                <p className="mt-2 text-xs text-stone-500">Use merge when this proposal is really an update to an existing card.</p>
              </div>
            </>
          )}

          {kind === 'field_update' && field && (
            <FieldProposalItem
              field={field}
              requestId={requestId}
              editing={editingField}
              draftValue={fieldDraft}
              savingAction={savingAction}
              onDraftChange={setFieldDraft}
              onStartEdit={() => {
                setEditingField(true);
                setFieldDraft(editableProposalValue(field.proposed_value));
              }}
              onCancelEdit={() => {
                setEditingField(false);
                setFieldDraft('');
              }}
              onReviewStatus={reviewField}
              onApplyEdited={applyEditedField}
            />
          )}

          {kind === 'card_attribute' && attribute && (
            <AttributeProposalItem
              attribute={attribute}
              editing={editingAttribute}
              draftValue={attributeDraft}
              rejectionReason={attributeRejectReason}
              savingAction={savingAction}
              onDraftChange={setAttributeDraft}
              onStartEdit={() => {
                setEditingAttribute(true);
                setAttributeDraft(initialAttributeDraft(attribute));
              }}
              onCancelEdit={() => {
                setEditingAttribute(false);
                setAttributeDraft('');
              }}
              onReview={reviewAttribute}
              onRejectionReasonChange={setAttributeRejectReason}
            />
          )}

          {['signal', 'match_signal_assignment'].includes(kind) && (
            <SignalProposalItem signal={payload} savingVerdict={savingAction} onVerdict={reviewSignal} />
          )}

          {kind === 'warning' && (
            isComputedWarning ? (
              <div className="rounded-xl border border-yellow-200 bg-yellow-50/70 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge kind="correction">Needs fix</Badge>
                  <p className="text-sm font-medium text-stone-900">{proposalTitle(item)}</p>
                </div>
                <p className="mt-2 text-sm text-stone-700">
                  This is a computed card gap. Fix the underlying card field and this proposal clears automatically on the next readiness refresh.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <ActionButton variant="primary" onClick={openPlace} disabled={!placeMasterId}>
                    Fix in Workbench
                  </ActionButton>
                  <ActionButton variant="secondary" onClick={onClose}>Close</ActionButton>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-red-200 bg-white p-4">
                <div className="flex flex-wrap gap-2">
                  <ActionButton variant="primary" onClick={() => setWarningVerdict('resolve')} disabled={Boolean(savingAction)}>
                    Resolve — verified/fixed
                  </ActionButton>
                  <ActionButton variant="danger" onClick={() => setWarningVerdict('dismiss')} disabled={Boolean(savingAction)}>
                    Dismiss — not applicable
                  </ActionButton>
                </div>
                <p className="mt-2 text-xs text-stone-500">
                  Resolve = the issue is verified or fixed. Dismiss = the warning does not apply.
                </p>
                {warningVerdict === 'resolve' && (
                  <div className="mt-4 rounded-lg border border-red-100 bg-red-50/60 p-3">
                    <label className="block">
                      <span className="text-xs font-medium text-red-800">
                        Evidence{requiresResolveEvidence ? ' required' : ''}
                      </span>
                      <textarea
                        rows={3}
                        value={warningEvidence}
                        onChange={event => setWarningEvidence(event.target.value)}
                        className="mt-1 w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                        placeholder="Source URL, call note, or what was fixed..."
                      />
                    </label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <ActionButton variant="primary" onClick={() => reviewWarning('resolve')} loading={savingAction === 'resolve'}>Confirm resolve</ActionButton>
                      <ActionButton variant="secondary" onClick={() => setWarningVerdict(null)} disabled={Boolean(savingAction)}>Cancel</ActionButton>
                    </div>
                  </div>
                )}
                {warningVerdict === 'dismiss' && (
                  <div className="mt-4 rounded-lg border border-red-100 bg-red-50/60 p-3">
                    <label className="block">
                      <span className="text-xs font-medium text-red-800">Reason required</span>
                      <textarea
                        rows={3}
                        value={warningDismissReason}
                        onChange={event => setWarningDismissReason(event.target.value)}
                        className="mt-1 w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                        placeholder="Why this warning is not actionable..."
                      />
                    </label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <ActionButton variant="danger" onClick={() => reviewWarning('dismiss')} loading={savingAction === 'dismiss'}>Confirm dismiss</ActionButton>
                      <ActionButton variant="secondary" onClick={() => setWarningVerdict(null)} disabled={Boolean(savingAction)}>Cancel</ActionButton>
                    </div>
                  </div>
                )}
              </div>
            )
          )}

          <div>
            {!noteOpen ? (
              <button type="button" onClick={() => setNoteOpen(true)} className="text-sm font-medium text-blue-700 hover:underline">
                Add note
              </button>
            ) : (
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">Reviewer note</span>
                <textarea
                  rows={3}
                  value={note}
                  onChange={event => setNote(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="Internal review note..."
                />
              </label>
            )}
          </div>

          <details className="rounded-lg border border-stone-200 bg-stone-50">
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
              Technical details
            </summary>
            <div className="border-t border-stone-200 p-3 text-xs text-stone-700">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="font-mono">{item.id}</span>
                <button type="button" onClick={copyTechnicalId} className="rounded border border-stone-300 bg-white px-2 py-1 text-[11px] font-medium text-stone-600 hover:bg-stone-50">
                  Copy ID
                </button>
              </div>
              <Row label="Kind" value={proposalKindLabel(kind)} />
              <Row label="Warning code" value={payload.warning_code} />
              <Row label="Scope" value={scope} />
              <Row label="Responsible" value={item.responsible_party || payload.responsible_party} />
              <Row label="Action meaning" value={proposalHelpText(kind, tier)} />
              <Row label="Created" value={item.created_at || payload.created_at} />
              <Row label="Updated" value={item.updated_at || payload.updated_at} />
              {sourceUrl && (
                <div className="mt-2">
                  <span className="text-stone-500">Source: </span>
                  <a href={sourceUrl} target="_blank" rel="noreferrer" className="break-all text-blue-700 hover:underline">{sourceUrl}</a>
                </div>
              )}
            </div>
          </details>
        </div>
      </div>
    </DraggableModal>
  );
}

function ProposalGroupModal({
  group,
  items,
  loading,
  error,
  onClose,
  onSelectItem,
  onSelectLabel,
  onOpenPlace,
}) {
  const placeMasterId = group?.place_master_id;
  const placeName = group?.place_name || placeMasterId || 'Unmatched proposals';

  return (
    <DraggableModal isOpen onClose={onClose} title={placeName} initialWidth={880} initialHeight={680}>
      <div className="h-full overflow-y-auto p-5">
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-stone-200 pb-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-stone-950">{placeName}</h3>
                {group?.area && <span className={pillClass('neutral')}>{group.area}</span>}
                <Badge kind={group?.tier === 'blocking' ? 'blocking' : group?.tier === 'correction' ? 'correction' : 'suggestion'}>
                  {groupTierLabel(group)}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-stone-500">
                {(group?.proposal_count || 0)} proposal{group?.proposal_count === 1 ? '' : 's'}
                {groupAttentionText(group) ? ` · ${groupAttentionText(group)}` : ''}
              </p>
              {(group?.proposal_labels || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {group.proposal_labels.map(label => (
                    <ProposalLabelPill
                      key={`${group.id}:${label.label}`}
                      label={label.label}
                      count={label.count}
                      tier={label.tier}
                      onClick={() => {
                        if (!loading) onSelectLabel?.(label.label);
                      }}
                      title={`Show ${label.label} proposals for this place`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {error && <ErrorAlert error={error} />}
          <ProposalLensSections
            items={items}
            loading={loading}
            emptyCopy="No proposals remain for this place."
            onSelectItem={onSelectItem}
            onOpenPlace={onOpenPlace}
          />
        </div>
      </div>
    </DraggableModal>
  );
}

export default function ChangeRequestsView({ onChangeRequestsChanged, filterPlaceId, filterCardKey, onOpenPlace }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [proposalItemTotal, setProposalItemTotal] = useState(0);
  const [blockingCount, setBlockingCount] = useState(0);
  const [facets, setFacets] = useState({});
  const [status, setStatus] = useState('active');
  const [tierFilter, setTierFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [labelFilter, setLabelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupLabel, setSelectedGroupLabel] = useState(null);
  const [groupItems, setGroupItems] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupError, setGroupError] = useState('');
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueSession, setQueueSession] = useState(null);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const groupedListMode = !filterPlaceId;

  const load = useCallback(async ({ append = false, cursor = null, summary = false } = {}) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = { status, include_gaps: groupedListMode ? 'false' : 'true' };
      if (summary && groupedListMode && !filterPlaceId) params.limit = '1';
      if (groupedListMode) params.group_by = 'place';
      if (filterPlaceId) params.place_master_id = filterPlaceId;
      if (filterCardKey) params.card_key = filterCardKey;
      if (tierFilter !== 'all') params.tier = tierFilter;
      if (typeFilter !== 'all') params.item_type = typeFilter;
      if (labelFilter !== 'all') params.label = labelFilter;
      if (sourceFilter !== 'all') params.source_type = sourceFilter;
      if (cursor) params.cursor = cursor;
      const res = await api.getProposalItems(params);
      const fetchedItems = res.items || [];
      if (summary && groupedListMode && !filterPlaceId) {
        setItems([]);
        setListLoaded(false);
      } else {
        setItems(current => append ? [...current, ...fetchedItems] : fetchedItems);
        setListLoaded(true);
      }
      setTotal(res.total || 0);
      setProposalItemTotal(res.proposal_item_total ?? res.total ?? 0);
      setBlockingCount(groupedListMode ? (res.blocking_group_count ?? res.blocking_count ?? 0) : (res.blocking_count || 0));
      setFacets(res.facets || {});
      setNextCursor(summary ? null : (res.next_cursor || null));
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [status, groupedListMode, filterPlaceId, filterCardKey, tierFilter, typeFilter, labelFilter, sourceFilter]);

  useEffect(() => {
    load({ summary: groupedListMode && !filterPlaceId });
  }, [filterPlaceId, groupedListMode, load]);

  const queueActive = Boolean(queueSession && !queueSession.completed);
  const queueProgress = queueActive ? `${queueSession.index + 1} / ${queueSession.items.length}` : '';
  const selected = items.find(item => item.id === selectedId)
    || groupItems.find(item => item.id === selectedId)
    || (queueActive ? queueSession.items.find(item => item.id === selectedId) : null)
    || null;

  const proposalQueryParams = useCallback((extra = {}) => {
    const params = { status, include_gaps: (filterPlaceId || filterCardKey) ? 'true' : 'false', ...extra };
    if (filterPlaceId) params.place_master_id = filterPlaceId;
    if (filterCardKey) params.card_key = filterCardKey;
    if (tierFilter !== 'all') params.tier = tierFilter;
    if (typeFilter !== 'all') params.item_type = typeFilter;
    if (labelFilter !== 'all') params.label = labelFilter;
    if (sourceFilter !== 'all') params.source_type = sourceFilter;
    return params;
  }, [status, filterPlaceId, filterCardKey, tierFilter, typeFilter, labelFilter, sourceFilter]);

  const placeGroupQueryParams = useCallback((group, label = null) => {
    const params = { status, limit: '500', include_gaps: 'true' };
    if (group?.place_master_id) params.place_master_id = group.place_master_id;
    if (group?.card_key) params.card_key = group.card_key;
    if (sourceFilter !== 'all') params.source_type = sourceFilter;
    if (label) params.label = label;
    return params;
  }, [status, sourceFilter]);

  const fetchQueueItems = useCallback(async () => {
    let cursor = null;
    const queueItems = [];
    do {
      const params = proposalQueryParams({ limit: '500' });
      if (cursor) params.cursor = cursor;
      const res = await api.getProposalItems(params);
      queueItems.push(...(res.items || []));
      cursor = res.next_cursor || null;
    } while (cursor && queueItems.length < 5000);
    return queueItems;
  }, [proposalQueryParams]);

  const openQueueItem = useCallback((index, session = queueSession) => {
    const item = session?.items?.[index];
    if (!item) return;
    setQueueSession(current => current ? { ...current, index } : current);
    setSelectedId(item.id);
  }, [queueSession]);

  const startQueue = async () => {
    setQueueLoading(true);
    setError('');
    try {
      const queueItems = await fetchQueueItems();
      if (queueItems.length === 0) return;
      const selectedIndex = selectedId ? queueItems.findIndex(item => item.id === selectedId) : -1;
      const session = {
        label: filterSummary,
        status,
        filterPlaceId,
        tierFilter,
        typeFilter,
        sourceFilter,
        items: queueItems,
        index: selectedIndex >= 0 ? selectedIndex : 0,
        startedAt: new Date().toISOString(),
      };
      setQueueSession(session);
      setSelectedId(session.items[session.index].id);
    } catch (e) {
      setError(e.message);
    } finally {
      setQueueLoading(false);
    }
  };

  const stopQueue = () => {
    setQueueSession(null);
  };

  const filterSummary = [
    labelize(status),
    filterPlaceId ? filterPlaceId : null,
    filterCardKey ? `Card ${filterCardKey}` : null,
    tierFilter !== 'all' ? labelize(tierFilter) : null,
    typeFilter !== 'all' ? labelize(typeFilter) : null,
    labelFilter !== 'all' ? labelFilter : null,
    sourceFilter !== 'all' ? labelize(sourceFilter) : null,
  ].filter(Boolean).join(' · ');

  const applyFilter = setter => value => {
    setter(value);
    setSelectedId(null);
    setSelectedGroup(null);
    setSelectedGroupLabel(null);
    setGroupItems([]);
    setItems([]);
    setListLoaded(false);
    setNextCursor(null);
  };

  const fetchGroupItems = useCallback(async (group, label = null) => {
    if (!group) return [];
    setSelectedGroup(group);
    setSelectedGroupLabel(label);
    setSelectedId(null);
    setGroupItems([]);
    setGroupLoading(true);
    setGroupError('');
    try {
      const params = placeGroupQueryParams(group, label);
      const res = await api.getProposalItems(params);
      let nextItems = res.items || [];
      if (!group.place_master_id && Array.isArray(group.item_ids) && group.item_ids.length > 0) {
        const ids = new Set(group.item_ids);
        nextItems = nextItems.filter(item => ids.has(item.id));
      }
      setGroupItems(nextItems);
      return nextItems;
    } catch (e) {
      setGroupError(e.message);
      return [];
    } finally {
      setGroupLoading(false);
    }
  }, [placeGroupQueryParams]);

  const openPlaceFromProposal = useCallback((placeMasterId, cardKey = 'primary') => {
    if (!placeMasterId || !onOpenPlace) return;
    if (queueActive) {
      const ok = window.confirm('Open this place in Workbench and stop the current queue?');
      if (!ok) return;
      setQueueSession(null);
    }
    onOpenPlace(placeMasterId, cardKey || 'primary', {
      source: 'changes',
      placeId: filterPlaceId || null,
      cardKey: filterCardKey || null,
    });
  }, [filterCardKey, filterPlaceId, onOpenPlace, queueActive]);

  const advanceQueue = useCallback(() => {
    if (!queueSession || queueSession.completed) return;
    const nextIndex = queueSession.index + 1;
    if (nextIndex >= queueSession.items.length) {
      setQueueSession(current => current ? { ...current, completed: true } : current);
      setSelectedId(null);
      return;
    }
    openQueueItem(nextIndex, queueSession);
  }, [openQueueItem, queueSession]);

  const previousQueueItem = useCallback(() => {
    if (!queueSession || queueSession.completed) return;
    const previousIndex = Math.max(0, queueSession.index - 1);
    if (previousIndex === queueSession.index) return;
    openQueueItem(previousIndex, queueSession);
  }, [openQueueItem, queueSession]);

  useEffect(() => {
    if (!queueActive) return undefined;
    const onKeyDown = event => {
      if (isEditableShortcutTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.toLowerCase() === 'j') {
        event.preventDefault();
        advanceQueue();
      } else if (event.key.toLowerCase() === 'k') {
        event.preventDefault();
        previousQueueItem();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [advanceQueue, previousQueueItem, queueActive]);

  const handleProposalActionComplete = async ({ close = false } = {}) => {
    const groupToRefresh = selectedGroup;
    const labelToRefresh = selectedGroupLabel;
    if (groupToRefresh) {
      let refreshedItems = await fetchGroupItems(groupToRefresh, labelToRefresh);
      if (labelToRefresh && refreshedItems.length === 0) {
        refreshedItems = await fetchGroupItems(groupToRefresh, null);
      }
      if (!close && !queueActive) {
        setSelectedId(refreshedItems[0]?.id || null);
      }
    } else {
      await load({ summary: groupedListMode && !listLoaded });
      setSelectedGroup(null);
      setGroupItems([]);
    }
    onChangeRequestsChanged?.();
    if (queueActive) {
      advanceQueue();
    } else if (close) {
      setSelectedId(null);
    }
  };

  const tierOptions = optionRowsWithFacetValues(
    TIER_FILTERS.map(([value, label]) => [value, label, value === 'all' ? proposalItemTotal : facetCount(facets, 'tier', value)]),
    facets,
    'tier',
  );
  const typeOptions = optionRowsWithFacetValues(
    TYPE_FILTERS.map(([value, label]) => [value, label, value === 'all' ? proposalItemTotal : facetCount(facets, 'item_type', value)]),
    facets,
    'item_type',
  );
  const sourceOptions = optionRowsWithFacetValues(
    SOURCE_FILTERS.map(([value, label]) => [value, label, value === 'all' ? proposalItemTotal : facetCount(facets, 'source_type', value)]),
    facets,
    'source_type',
  );
  const labelOptions = [
    ['all', 'All proposals', proposalItemTotal],
    ...((facets.label || []).map(row => [row.value, row.label || row.value, row.count])),
  ];
  const labelMeta = useMemo(() => proposalLabelMetaFromItems(items, groupedListMode), [items, groupedListMode]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Change Proposals</h1>
          <p className="text-sm text-gray-500">
            {groupedListMode ? `${total} places` : `${total} review items`}
            {groupedListMode && proposalItemTotal !== total ? ` · ${proposalItemTotal} proposals` : ''}
            {blockingCount > 0 ? ` · ${blockingCount} blocking` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {queueActive && (
            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800">
              Queue: {queueSession.label} · {queueProgress}
            </span>
          )}
          {queueSession?.completed && (
            <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-800">
              Queue complete
            </span>
          )}
          {queueActive && (
            <>
              <ActionButton variant="secondary" onClick={previousQueueItem}>Previous</ActionButton>
              <ActionButton variant="secondary" onClick={advanceQueue}>Skip</ActionButton>
            </>
          )}
          <ActionButton
            variant={queueActive ? 'secondary' : 'primary'}
            onClick={queueActive ? stopQueue : startQueue}
            loading={queueLoading}
            disabled={!queueActive && total === 0}
          >
            {queueActive ? 'Stop queue' : 'Start queue'}
          </ActionButton>
          <button
            onClick={() => load({ summary: false })}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 text-gray-600" /> Load results
          </button>
        </div>
      </div>

      {error && <ErrorAlert error={error} />}

      {filterPlaceId && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          {filterCardKey && onOpenPlace && (
            <button
              type="button"
              onClick={() => onOpenPlace(filterPlaceId, filterCardKey)}
              className="font-medium underline underline-offset-2"
            >
              Back to card
            </button>
          )}
          <span>Filtered: <span className="font-mono font-medium">{filterPlaceId}</span></span>
          {filterCardKey && <span className={pillClass('blue')}>Card: {filterCardKey}</span>}
        </div>
      )}

      <select
        value={status}
        onChange={event => {
          setStatus(event.target.value);
          setSelectedId(null);
          setSelectedGroup(null);
          setSelectedGroupLabel(null);
          setGroupItems([]);
          setItems([]);
          setListLoaded(false);
          setNextCursor(null);
        }}
        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
      >
        {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
      </select>

      <div className="space-y-2 rounded-xl border border-stone-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-16 text-xs font-semibold uppercase tracking-wider text-stone-400">Tier</span>
          {tierOptions.map(([value, label, count]) => (
            <FilterChip
              key={value}
              kind={value === 'all' ? 'neutral' : value}
              active={tierFilter === value}
              onClick={() => applyFilter(setTierFilter)(value)}
            >
              {facetChipText(label, count)}
            </FilterChip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-16 text-xs font-semibold uppercase tracking-wider text-stone-400">Type</span>
          {typeOptions.map(([value, label, count]) => (
            <FilterChip
              key={value}
              kind={value === 'all' ? 'neutral' : typeTone(value)}
              active={typeFilter === value}
              onClick={() => applyFilter(setTypeFilter)(value)}
            >
              {facetChipText(label, count)}
            </FilterChip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-16 text-xs font-semibold uppercase tracking-wider text-stone-400">Proposals</span>
          {labelOptions.map(([value, label, count]) => (
            value === 'all' ? (
              <FilterChip key={value} active={labelFilter === value} onClick={() => applyFilter(setLabelFilter)(value)}>
                {facetChipText(label, count)}
              </FilterChip>
            ) : (
              <ProposalLabelPill
                key={value}
                label={label}
                count={count}
                tier={labelMeta.get(value)?.tier || 'suggestion'}
                active={labelFilter === value}
                onClick={() => applyFilter(setLabelFilter)(value)}
                title={`Filter to ${label}`}
              />
            )
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-16 text-xs font-semibold uppercase tracking-wider text-stone-400">Source</span>
          {sourceOptions.map(([value, label, count]) => (
            <FilterChip
              key={value}
              kind={value === 'all' ? 'neutral' : sourceTone(value)}
              active={sourceFilter === value}
              onClick={() => applyFilter(setSourceFilter)(value)}
            >
              {facetChipText(label, count)}
            </FilterChip>
          ))}
        </div>
        <p className="text-xs text-stone-500">Start queue uses the current filters: {filterSummary || 'All active proposals'}.</p>
      </div>

      {filterPlaceId ? (
        <div className="rounded-xl border border-gray-200 bg-stone-50/50 p-4 shadow-sm">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-stone-200 pb-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold text-stone-950">Proposals for this {filterCardKey ? 'card' : 'place'}</h2>
                {filterCardKey && <span className={pillClass('blue')}>Card: {filterCardKey}</span>}
                {blockingCount > 0 && <Badge kind="blocking">{blockingCount} blocking</Badge>}
              </div>
              <p className="mt-1 text-sm text-stone-500">{proposalItemTotal} active proposal{proposalItemTotal === 1 ? '' : 's'} in this lens.</p>
            </div>
            <ActionButton variant="secondary" onClick={startQueue} loading={queueLoading} disabled={proposalItemTotal === 0 || queueActive}>
              Queue these {proposalItemTotal}
            </ActionButton>
          </div>
          <ProposalLensSections
            items={items}
            loading={loading}
            emptyCopy="No proposals remain for this card."
            onSelectItem={item => setSelectedId(item.id)}
            onOpenPlace={openPlaceFromProposal}
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading...</div>
          ) : !listLoaded ? (
            <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-center text-sm text-gray-500">
              <p className="font-medium text-gray-700">{proposalItemTotal} proposal{proposalItemTotal === 1 ? '' : 's'} match the current filters.</p>
              <p>Use Load results or Start queue when you are ready to review them. The full proposal list is no longer loaded automatically.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Place</th>
                  <th className="px-4 py-3 text-left font-medium">Proposals</th>
                  <th className="px-4 py-3 text-left font-medium">Tier</th>
                  <th className="px-4 py-3 text-left font-medium">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(group => (
                  <tr key={group.id} onClick={() => fetchGroupItems(group)} className="cursor-pointer hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600">
                      {group.place_master_id ? (
                        <button
                          type="button"
                          onClick={event => {
                            event.stopPropagation();
                            openPlaceFromProposal(group.place_master_id, 'primary');
                          }}
                          className="text-left"
                        >
                          <span className="block font-medium text-gray-900 underline-offset-2 hover:underline">{group.place_name || group.place_master_id}</span>
                          <span className="block text-xs text-gray-400">{[group.area, group.place_master_id].filter(Boolean).join(' · ')}</span>
                        </button>
                      ) : (
                        <>
                          <p className="font-medium text-gray-900">{group.place_name || 'Unmatched proposal'}</p>
                          <p className="text-xs text-gray-400">Not attached to a place</p>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {(group.proposal_labels || []).map(label => (
                          <ProposalLabelPill
                            key={`${group.id}:${label.label}`}
                            label={label.label}
                            count={label.count}
                            tier={label.tier}
                            onClick={event => {
                              event.stopPropagation();
                              fetchGroupItems(group, label.label);
                            }}
                            title={`Open ${label.label} proposals for this place`}
                          />
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-gray-400">{group.proposal_count} total</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge kind={group.tier === 'blocking' ? 'blocking' : group.tier === 'correction' ? 'correction' : 'suggestion'}>
                        {groupTierLabel(group)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{groupSourceText(group)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && listLoaded && items.length === 0 && <p className="py-10 text-center text-sm text-gray-400">No change proposals found</p>}
        </div>
      )}

      {nextCursor && (
        <div className="flex justify-center">
          <ActionButton variant="secondary" onClick={() => load({ append: true, cursor: nextCursor })} loading={loadingMore}>
            Load more
          </ActionButton>
        </div>
      )}

      {selectedGroup && !selected && (
        <ProposalGroupModal
          group={selectedGroup}
          items={groupItems}
          loading={groupLoading}
          error={groupError}
          onClose={() => {
            setSelectedGroup(null);
            setSelectedGroupLabel(null);
            setGroupItems([]);
            setGroupError('');
          }}
          onSelectItem={item => setSelectedId(item.id)}
          onSelectLabel={label => fetchGroupItems(selectedGroup, label)}
          onOpenPlace={openPlaceFromProposal}
        />
      )}

      {selected && (
        <ProposalItemModal
          item={selected}
          onClose={() => setSelectedId(null)}
          onActionComplete={handleProposalActionComplete}
          queueActive={queueActive}
          queueProgress={queueProgress}
          onSkipQueueItem={advanceQueue}
          onPreviousQueueItem={previousQueueItem}
          onOpenPlace={openPlaceFromProposal}
        />
      )}
    </div>
  );
}
